import { AdBoardEngagement } from '@keyops-cep/api-client';

import { WithSubRows } from '../../utils/functions/format-feed-data';

const AdBoardStates = ['draft', 'planning', 'live', 'closed'] as const;
type AdBoardState = (typeof AdBoardStates)[number];

export type AdBoardOutletContextType = {
  adBoardEngagementDetails: AdBoardEngagement;
  adBoardData: AdBoardDto;
  executiveSummaryData: {
    attendees: number;
    completionRate: string;
    executiveSummaryIntro: string;
    executiveSummaryRecommendations: string;
  };
  feedData: Array<AdBoardSectionDataFeedDto>;
  participantsData: Array<AdBoardEngagementParticipantsDto>;
  generalDiscussionData: Array<AdBoardGeneralDiscussionDto>;
};

// TODO: use generated styles
export type AdBoardDto = {
  id: string;
  title: string;
  description?: string;
  objectives: string;
  generalDiscussionId?: string;
  startDate?: Date;
  endDate?: Date;
  createdAt: Date;
  updatedAt: Date;
  sections: SectionDto[];
  sectionGroups: AdBoardSectionGroupDto[];
  state: AdBoardState;
};

type BaseAdBoardSectionDto = {
  id: string;
  title: string;
  description?: string;
  index: number;
  adBoardId: string;
  sectionGroupId: string;
};

type VideoDto = {
  id: string;
  title: string;
  createdAt?: string;
  updatedAt?: string;
  description?: string;
  link: string;
  totalRunTime: number; // store in seconds, e.g. a 10 minute video would be 600
};

type AttachmentDto = {
  id: string;
  title: string;
  description?: string;
  contentType: unknown;
  readingTime: number;
  link: string;
  createdAt?: string;
  updatedAt?: string;
};

type SurveyDto = {
  id: string;
  surveySparrowId: string;
  surveyChannelId?: string;
  createdAt: string;
  updatedAt: string;
};

type DiscussionDto = {
  id: string;
  topic: string;
  active: boolean;
  createdAt: string;
  updatedAt: string;
  messages?: DiscussionMessageDto[];
};

export type DocumentMarkupDto = {
  id: string;
  content: string; //the html of the document
  discussionId: string;
  createdAt?: string;
  updatedAt?: string;
};

type DiscussionMessageDto = {
  id: string;
  content: string;
  discussionId: string; // The discussion it belongs to
  // The message author can either be an HCP or an admin (moderator in the future)
  userId?: string;
  user?: Partial<unknown>; // We may need some of the author info
  adminId?: string;
  admin?: Partial<unknown>; // We may need some of the author info
  createdAt: string;
  updatedAt: string;
};

export type VideoAdBoardSectionDto = BaseAdBoardSectionDto & {
  type: 'video';
  content: { id: string; videoId: string; video: VideoDto };
};

export type AttachmentAdBoardSectionContentDto = {
  id: string;
  attachmentId: string;
  index: number;
  attachment: AttachmentDto;
};

export type AttachmentsAdBoardSectionDto = BaseAdBoardSectionDto & {
  type: 'attachment';
  content: AttachmentAdBoardSectionContentDto[];
};

type SurveyAdBoardSectionDto = BaseAdBoardSectionDto & {
  type: 'survey';
  content: {
    id: string;
    surveyId: string;
    survey: SurveyDto;
    surveyEngagementId?: string;
  };
};

// Discussion section
export type DiscussionAdBoardSectionContentDto = {
  id: string;
  discussionId: string;
  index: number;
  discussion: DiscussionDto;
};
type DiscussionsAdBoardSectionDto = BaseAdBoardSectionDto & {
  type: 'discussion';
  content: DiscussionAdBoardSectionContentDto[];
};

// Document markup section
export type DocumentMarkupAdBoardSectionContentDto = {
  id: string;
  documentMarkupId: string;
  requiredCommentCount: number;
  documentMarkup: DocumentMarkupDto;
};
export type DocumentMarkupAdBoardSectionDto = BaseAdBoardSectionDto & {
  type: 'documentMarkup';
  content: DocumentMarkupAdBoardSectionContentDto;
};

//note: need to update in case of more activity types
export type SectionDto =
  | AttachmentsAdBoardSectionDto
  | DiscussionsAdBoardSectionDto
  | SurveyAdBoardSectionDto
  | VideoAdBoardSectionDto
  | DocumentMarkupAdBoardSectionDto;

export type AdBoardSectionGroupDto = {
  id: string;
  title: string;
  description?: string;
  index: number;
  adBoardId: string;
  estimatedTime: number;
  createdAt: string;
  updatedAt: string;
  sections?: SectionDto[];
};

type DiscussionMessageContextDto = {
  mentions: string[]; // array of userIds, empty if not mentions
};

export type VideoMessageContextDto = DiscussionMessageContextDto & {
  videoTimeStamp: number;
};

export type DocumentMarkupMessageContextDto = DiscussionMessageContextDto & {
  startIndex: number;
  endIndex: number;
  highlightedText: string;
};

export type MessageContext =
  | DiscussionMessageContextDto
  | VideoMessageContextDto
  | DocumentMarkupMessageContextDto;

const AdBoardSectionTypes = [
  'attachment',
  'discussion',
  'video',
  'survey',
  'documentMarkup',
] as const;

export type AdBoardSectionType = (typeof AdBoardSectionTypes)[number];

export type AdBoardDataFeedDto = {
  id: number;
  participantName: string;
  threadId?: string;
  messageId: string;
  editedAt?: string;
  createdAt: string;
  hidden?: boolean;
  content?: string;
  discussionId?: string;
  messageContext?: MessageContext;
  // populated by FE for tables
  parentMessageContent?: string;
};

export interface AdBoardSectionDataFeedDto extends AdBoardDataFeedDto {
  userId: number;
  sectionId: string;
  sectionIndex: number;
  sectionTitle: string;
  sectionType: AdBoardSectionType;
  sectionGroupId: string;
  sectionGroupIndex: number;
  sectionGroupTitle: string;
  attachmentName?: string;
  attachmentOpenedAt?: string;
}

export interface AdBoardSectionDataFeedDtoWithSubRows
  extends AdBoardSectionDataFeedDto,
    WithSubRows<AdBoardSectionDataFeedDto> {}

export interface AdBoardGeneralDiscussionDto extends AdBoardDataFeedDto {
  userId?: number;
  adminId?: number;
}

export interface AdBoardGeneralDiscussionDtoWithSubRows
  extends AdBoardGeneralDiscussionDto,
    WithSubRows<AdBoardGeneralDiscussionDto> {}

export const AdBoardParticipantRoles = ['participant', 'moderator'] as const;
export type AdBoardParticipantRole = (typeof AdBoardParticipantRoles)[number];

export const ParticipantSubtypes = ['default'];
export type ParticipantSubtype = (typeof ParticipantSubtypes)[number];

export const ModeratorSubtypes = ['speaker', 'default', 'hidden'];
export type ModeratorSubtype = (typeof ModeratorSubtypes)[number];

export const AdBoardParticipantRoleSubTypes = [
  ...ParticipantSubtypes,
  ...ModeratorSubtypes,
];

export type AdBoardParticipantRoleSubType =
  (typeof AdBoardParticipantRoleSubTypes)[number];

export type AdBoardEngagementParticipantsDto = {
  adBoardId: string;
  completionPercentage: number;
  honorarium: number;
  id: string;
  inviteId: string;
  userId: number;
  progress: AdBoardProgressDto;
  createdAt: string;
  updatedAt: string;
  user?: { firstName: string; lastName: string; speciality?: string[] }; // Only firstName, lastName and specialty
  role: AdBoardParticipantRole;
  roleSubType: AdBoardParticipantRoleSubType;
};

type AdBoardProgressDto = {
  id: string;
  latestSection?: string;
  progressDetails: ProgressDetails;
  version: string;
  startedOn?: string;
  finishedOn?: string;
  createdAt: string;
  updatedAt: string;
};

// Base progress type
type BaseAdBoardSectionProgress = {
  sectionId: string;
  finished?: boolean;
  accessible: boolean;
};

// Extension progress types
type AdBoardVideoSectionProgressDto = { currentTime: number };
type FinishedAttachments = { attachmentId: string; timestamp: string };
type AdBoardAttachmentSectionProgressDto = {
  finishedAttachments: FinishedAttachments[];
};
type AdBoardSurveySectionProgressDto = { uniqueLink: string };
type AdBoardDiscussionSectionProgressDto = { finishedTopics: string[] };

// Intersection for each specific section progress type
type AdBoardVideoSectionProgressCombined = BaseAdBoardSectionProgress &
  AdBoardVideoSectionProgressDto;
type AdBoardAttachmentSectionProgressCombined = BaseAdBoardSectionProgress &
  AdBoardAttachmentSectionProgressDto;
type AdBoardSurveySectionProgressCombined = BaseAdBoardSectionProgress &
  AdBoardSurveySectionProgressDto;
type AdBoardDiscussionSectionProgressCombined = BaseAdBoardSectionProgress &
  AdBoardDiscussionSectionProgressDto;

// Union of the two combined types
type AdBoardSectionProgressDto =
  | AdBoardVideoSectionProgressCombined
  | AdBoardAttachmentSectionProgressCombined
  | AdBoardSurveySectionProgressCombined
  | AdBoardDiscussionSectionProgressCombined;

interface ProgressDetails {
  sectionProgress: AdBoardSectionProgressDto[];
}

export const isVideoMessageContext = (
  messageContext: MessageContext | undefined
): messageContext is VideoMessageContextDto => {
  return !!(
    messageContext &&
    (messageContext as VideoMessageContextDto).videoTimeStamp !== undefined
  );
};
